/**
 * lib/cards/defaults/wave.js — Minimal template default bottom art.
 *
 * Three-layer sine-wave band tinted from the tenant's brand colour.
 * Drawn mathematically rather than from an SVG/PNG so it scales to
 * any tenant's colour without preprocessing.
 *
 * Extracted from the original card-export.js (Coastal Verify v1
 * geometry, locked with Dani on Apr 20 2026 — don't change widths or
 * wavelengths without Dani's explicit sign-off).
 */

import { CR80, premultiplyAlpha } from '../shared.js';

/**
 * Draw a wave band at a given Y, using the brand colour at a chosen
 * alpha. jsPDF's free build doesn't support per-path opacity, so we
 * pre-multiply against white.
 *
 * @param pdf          jsPDF instance
 * @param y            top of the wave band in mm
 * @param brandColor   { r, g, b } 0-255
 * @param alpha        0.0 - 1.0
 */
export function drawWaveLayer(pdf, y, brandColor, alpha) {
  const { r, g, b } = premultiplyAlpha(brandColor, alpha);
  pdf.setFillColor(r, g, b);

  // 100 narrow columns, each offset by sine wave — simpler and more
  // reliable than jsPDF curve primitives; prints cleanly at any DPI.
  const amplitude = 1.2;
  const wavelength = 18;
  const cols = 100;
  const colWidth = CR80.widthMm / cols;

  for (let i = 0; i < cols; i++) {
    const x = i * colWidth;
    const phase = (x / wavelength) * Math.PI * 2;
    const topY = y + Math.sin(phase) * amplitude;
    const colHeight = CR80.heightMm - topY;
    // +0.05 overlap prevents hairline gaps between columns at print resolution
    pdf.rect(x, topY, colWidth + 0.05, colHeight, 'F');
  }
}

/**
 * Paint the full Minimal-template bottom band (3 waves + solid footer).
 *
 * @param pdf          jsPDF
 * @param brandColor   { r, g, b }
 * @param footerY      top Y of the solid brand-colour footer band (mm)
 */
export function drawWaveBand(pdf, brandColor, footerY = 49) {
  // Three layers of wave above the footer, progressively more opaque
  drawWaveLayer(pdf, footerY - 3, brandColor, 0.15);
  drawWaveLayer(pdf, footerY - 1.5, brandColor, 0.25);
  drawWaveLayer(pdf, footerY - 0.5, brandColor, 0.45);

  // Solid brand-coloured footer strip
  pdf.setFillColor(brandColor.r, brandColor.g, brandColor.b);
  pdf.rect(0, footerY, CR80.widthMm, CR80.heightMm - footerY, 'F');
}
